<template>
  <div class="product-form">
    <Spin v-if="loading"></Spin>
    <Form v-else ref="productForm" :model="formData" :rules="ruleValidate" label-position="top">
      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('product.info.product_name')" prop="product_name">
            <Input v-model="formData.product_name"></Input>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem :label="$t('product.info.english_name')" prop="english_name" :rules="{ required: true, message: $t('product.english_name_is_required') }">
            <Input v-model="formData.english_name"></Input>
            <div class="text-align-right" style="font-size: 12px;">{{$t('product.info.english_name_help')}}</div>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('product.info.product_type')" prop="product_type">
            <Select v-model="formData.product_type" :disabled="!isNew" transfer>
              <Option value="product">{{ $t('product.info.product_type_product') }}</Option>
              <Option value="service">{{ $t('product.info.product_type_service') }}</Option>
            </Select>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem :label="$t('product.info.product_number')" prop="product_number" :required="isServiceType">
            <Input v-model="formData.product_number" :disabled="preventProductNumberChange">
              <Button icon="ios-refresh" slot="append" @click="handleGenerateProductNumber" :disabled="!!(formData.id && formData.product_number)"></Button>
            </Input>
            <div class="text-align-right" style="font-size: 12px;">Unique code, such as, SKU.</div>
          </FormItem>
        </Col>
      </Row>
      <div v-if="formData.product_type">
        <div v-if="formData.product_type === 'product'">
          <Row :gutter="32">
            <Col span="12">
              <FormItem :label="$t('product.info.product_alias')">
                <Input v-model="formData.product_alias"></Input>
              </FormItem>
            </Col>
            <Col span="12">
              <FormItem :label="$t('product.info.product_model')">
                <Input v-model="formData.model"></Input>
              </FormItem>
            </Col>
          </Row>
          <Row :gutter="32">
            <Col span="12">
              <FormItem :label="$t('product.info.customer')">
                <AccountSearchAndPickWidget :account.sync="formData.customer" v-model="formData.customer_id"></AccountSearchAndPickWidget>
              </FormItem>
            </Col>
            <Col span="12">
              <FormItem :label="$t('product.info.category')" prop="data.category" required>
                <Select v-model="formData.data.category" @on-change="handleProductCategoryChange" transfer>
                  <Option value="turnkey">Turnkey</Option>
                  <Option value="pcba">PCBA</Option>
                  <Option value="pcb">PCB</Option>
                  <Option value="accessory">Accessory</Option>
                  <Option value="mould_tooling">Mould tooling</Option>
                </Select>
              </FormItem>
            </Col>
          </Row>
          <Row :gutter="32">
            <Col span="12">
              <FormItem :label="$t('product.info.lifecycle_status')" prop="lifecycle_status" required>
                <Select v-model="formData.lifecycle_status">
                  <Option value="active">{{ $t('product.info.product_status_active') }}</Option>
                  <Option value="discontinued">{{ $t('product.info.product_status_discontinued') }}</Option>
                </Select>
              </FormItem>
            </Col>
            <Col span="12">
              <FormItem :label="$t('product.info.webpagelink')">
                <Input v-model="formData.webpage_link" />
              </FormItem>
            </Col>
          </Row>
        </div>
        <Row :gutter="32" v-if="isServiceType">
          <Col span="12">
            <FormItem :label="$t('product.info.category')" required>
              <Select v-model="formData.data.category">
                <OptionGroup label="Design related">
                  <Option value="layout">Layout</Option>
                  <Option value="consulting">Consulting</Option>
                  <Option value="dfm_solution_fee">DFM</Option>
                  <Option value="mechanical_design">Mechanical design</Option>
                  <Option value="sample">Sample</Option>
                </OptionGroup>
                <OptionGroup label="Certification related">
                  <Option value="certification_application">Certification application</Option>
                  <Option value="material_cost_testing">Material cost testing</Option>
                  <Option value="rework">Rework</Option>
                  <Option value="certification_management">Certification management</Option>
                </OptionGroup>
                <OptionGroup label="Testing & rework">
                  <Option value="testing_verification_cost">Testing verification</Option>
                  <Option value="testing_batch">Batch testing</Option>
                </OptionGroup>
                <OptionGroup label="Logistics related">
                  <Option value="shipping">Shipping</Option>
                  <Option value="operation">Operation</Option>
                </OptionGroup>
              </Select>
            </FormItem>
          </Col>
          <Col span="12">
            <FormItem :label="$t('product.info.lifecycle_status')" prop="lifecycle_status" required>
              <Select v-model="formData.lifecycle_status">
                <Option value="active">{{ $t('product.info.product_status_active') }}</Option>
                <Option value="discontinued">{{ $t('product.info.product_status_discontinued') }}</Option>
              </Select>
            </FormItem>
          </Col>
          <Col span="12">
            <FormItem :label="$t('product.info.webpagelink')" prop="webpage_link">
              <Input v-model="formData.webpage_link" />
            </FormItem>
          </Col>
        </Row>
      </div>
      <Row :gutter="32">
        <Col span="24">
          <FormItem :label="$t('description')">
            <Input type="textarea" v-model="formData.description" :rows="6" />
          </FormItem>
        </Col>
      </Row>
      <div class="text-align-right actions">
        <Button type="primary" @click="handleSubmit('productForm')" size="large">{{ $t('save') }}</Button>
      </div>
    </Form>
  </div>
</template>

<script>
import AccountSearchAndPickWidget from '@/view/account/components/widgets/account-search-and-pick-widget.vue'
import Editor from '@/components/editor'
import Money from '@/components/inputs/money'
import CurrencySelect from '@/components/currency'
import { generateProductNumber } from '@/api/product'
import OrderItemForm from '@/view/order/forms/order-item-form.vue'
export default {
  name: 'ProductForm',
  components: {
    AccountSearchAndPickWidget,
    Editor,
    Money,
    CurrencySelect,
    OrderItemForm
  },
  props: {
    product: {
      type: Object,
      required: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      formData: {
        data: {
          category: '',
          unit_cost: 0,
          unit_price: 0,
          currency: 'CNY'
        }
      },
      customers: [],
      productTypes: [
        { label: 'PCB', key: 'pcb' },
        { label: 'PCBA', key: 'pcba' },
        { label: 'Turnkey', key: 'turnkey' },
        { label: 'Layout', key: 'layout' }
      ],
      ruleValidate: {
        product_name: [
          { required: true, message: 'Product name cannot be empty!', trigger: 'change' }
        ],
        english_name: [
          {
            validator(rule, value, callback, source, options) {
              const errors = []
              if (!/^[^\u4e00-\u9fa5]+$/.test(value)) {
                errors.push(new Error('English name should not contains chinese characters.'))
              }
              return errors
            }
          }
        ],
        product_type: [
          { required: true, message: 'Product type cannot be empty!', trigger: 'change' }
        ],
        name: [
          { required: true, message: 'Customer cannot be empty!', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    marginValue () {
      return this.formData.data.unit_price - this.formData.data.unit_cost
    },
    isNew () {
      return !this.formData.id
    },
    isServiceType () {
      return this.formData.product_type === 'service'
    },
    preventProductNumberChange () {
      return !!(this.formData.id && this.formData.product_number)
    }
  },
  methods: {
    generateProductNumber,
    handleSubmit (ref) {
      this.$refs[ref].validate((valid) => {
        if (valid) {
          this.$emit('save-product', this.formData)
        }
      })
    },
    handleProductCategoryChange (value) {
      this.formData.product_data_type = `product__${value}_data_type`
    },
    handleGenerateProductNumber () {
      const prefix = this.formData.product_type === 'service' ? 'FY-' : 'PD-'
      this.generateProductNumber(prefix)
        .then(resp => {
          this.formData = Object.assign({}, this.formData, { product_number: resp.data })
        })
    }
  },
  watch: {
    'formData.product_type': function (val) {
      if (val && val === 'service') {
        this.formData.product_data_type = `product__service_data_type`
      }
    },
    loading: function () {
      this.formData = Object.assign({}, this.formData, this.product)
    }
  }
}
</script>
